// =============================================================================
// PageBackground Component
// =============================================================================
// Provides a zen-like background image for pages with proper layering.
// Uses session-based random image selection for variety across sessions.
//
// Architecture:
// ├── Background Container (fixed, z-index: -10, pointer-events: none)
// │   ├── <img> element (full cover, 55% opacity)
// │   └── Gradient Overlay (lighter white overlay for more prominent images)
// └── Children (content, relative z-index: 0)
//
// Critical: Content cards should maintain their bg-white backgrounds
// to ensure text readability against the background image.
// =============================================================================

import { useEffect, useState } from 'react';
import { getSessionBackgroundUrl } from '@/lib/session-background';

interface PageBackgroundProps {
  children: React.ReactNode;
  /**
   * Optional: Override the default opacity (0.55 = 55%)
   * Recommended range: 0.4 - 0.65
   */
  opacity?: number;
  /**
   * Optional: Disable the background (useful for modals/overlays)
   */
  disabled?: boolean;
}

export function PageBackground({
  children,
  opacity = 0.55,
  disabled = false,
}: PageBackgroundProps) {
  const [bgUrl, setBgUrl] = useState<string | null>(null);
  const [imageLoaded, setImageLoaded] = useState(false);

  useEffect(() => {
    // Get session background on mount (client-side only)
    // This prevents hydration mismatch with SSR
    if (!disabled) {
      setBgUrl(getSessionBackgroundUrl());
    }
  }, [disabled]);

  // Don't render background if disabled
  if (disabled) {
    return <>{children}</>;
  }

  return (
    <>
      {/* Background Layer - Fixed, behind everything */}
      <div
        className="fixed inset-0 -z-10 pointer-events-none overflow-hidden"
        aria-hidden="true"
      >
        {/* Background Image */}
        {bgUrl && (
          <img
            src={bgUrl}
            alt=""
            className={`
              w-full h-full object-cover
              transition-opacity duration-500
              ${imageLoaded ? '' : 'opacity-0'}
            `}
            style={{ opacity: imageLoaded ? opacity : 0 }}
            onLoad={() => setImageLoaded(true)}
            // Prevent image from being dragged
            draggable={false}
          />
        )}

        {/* Gradient Overlay for readability + subtle brand presence */}
        {/* Gradient: white(45%) → white(30%) → brand-teal(5%) */}
        <div
          className={`
            absolute inset-0
            bg-gradient-to-br from-white/45 via-white/30 to-[#17DDAB]/5
            transition-opacity duration-500
            ${imageLoaded ? 'opacity-100' : 'opacity-0'}
          `}
        />

        {/* Fallback background color while image loads */}
        <div
          className={`
            absolute inset-0 bg-gray-50
            transition-opacity duration-300
            ${imageLoaded ? 'opacity-0' : 'opacity-100'}
          `}
        />
      </div>

      {/* Content Layer - Positioned above background */}
      <div className="relative z-0 min-h-screen">
        {children}
      </div>
    </>
  );
}

// =============================================================================
// Alternative: Background-only component (for more granular control)
// =============================================================================

interface BackgroundLayerProps {
  opacity?: number;
}

/**
 * Standalone background layer component.
 * Use this when you need more control over the content structure.
 * Must be placed as the first child in a container.
 */
export function BackgroundLayer({ opacity = 0.55 }: BackgroundLayerProps) {
  const [bgUrl, setBgUrl] = useState<string | null>(null);
  const [imageLoaded, setImageLoaded] = useState(false);

  useEffect(() => {
    setBgUrl(getSessionBackgroundUrl());
  }, []);

  return (
    <div
      className="fixed inset-0 -z-10 pointer-events-none overflow-hidden"
      aria-hidden="true"
    >
      {bgUrl && (
        <img
          src={bgUrl}
          alt=""
          className={`
            w-full h-full object-cover
            transition-opacity duration-500
            ${imageLoaded ? '' : 'opacity-0'}
          `}
          style={{ opacity: imageLoaded ? opacity : 0 }}
          onLoad={() => setImageLoaded(true)}
          draggable={false}
        />
      )}
      <div
        className={`
          absolute inset-0
          bg-gradient-to-br from-white/45 via-white/30 to-[#17DDAB]/5
          transition-opacity duration-500
          ${imageLoaded ? 'opacity-100' : 'opacity-0'}
        `}
      />
      <div
        className={`
          absolute inset-0 bg-gray-50
          transition-opacity duration-300
          ${imageLoaded ? 'opacity-0' : 'opacity-100'}
        `}
      />
    </div>
  );
}
